import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { useScCategoryMappingQuery } from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import * as state from '@/components/Analysis/state';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { PillStatus } from '@/components/shared/Pill/types';
import { Pill } from '@/components/shared/Pill';
import { upperSnakeCase } from '@/utils';
import { NOT_APPLICABLE_LABEL } from '@/constants';
import { useAssemblyData } from './useAssemblyData';

const getStatus = (label: string): PillStatus => {
  switch (label) {
    case 'ALARM_LEVEL_1':
      return 'warning';
    case 'ALARM_LEVEL_2':
      return 'degraded';
    case 'ALARM_LEVEL_3':
      return 'failed';
    case 'CLEAN':
      return 'success';
    case 'SC1':
      return 'warning';
    case 'SC2':
      return 'degraded';
    case 'SC3':
      return 'failed';
    case 'SC0':
      return 'success';
    default:
      return 'disabled';
  }
};

export const useGetDefectPill = (params?: { assemblyId?: string }) => {
  const structureId = useGetStructureId();
  const selectedAssemblyId = useAtomValue(state.selectedAssemblyId);

  const { assemblyDetails } = useAssemblyData({
    assemblyId: params?.assemblyId || selectedAssemblyId,
  });

  const { data } = useScCategoryMappingQuery({
    variables: { structureId },
    skip: !structureId,
  });

  const scCategoryMappingExists = useMemo(() => {
    return Object.keys(data?.structure?.scCategoryMapping ?? {}).length > 0;
  }, [data?.structure?.scCategoryMapping]);

  const defectLabel = useMemo(() => {
    return scCategoryMappingExists
      ? assemblyDetails?.governing_sc_category
      : assemblyDetails?.recommendation_alarm_level;
  }, [
    assemblyDetails?.governing_sc_category,
    assemblyDetails?.recommendation_alarm_level,
    scCategoryMappingExists,
  ]);

  return (
    <Pill
      label={defectLabel ?? NOT_APPLICABLE_LABEL}
      showIcon
      icon={<NotificationsIcon />}
      status={getStatus(upperSnakeCase(defectLabel ?? ''))}
      state="default"
      size="medium"
      disabled
    />
  );
};
